import { findLocaleKeys, getCanonicalLocaleKey } from "../../../core/i18n/registry.js";
import { DEFAULT_LOCALE } from "../../../i18n.js";
import { definePlayerFeature } from "../../feature.js";
import { findTrackElement, isCaptionOrSubtitleTrack, listen } from "@videojs/utils/dom";
import { isNil } from "@videojs/utils/predicate";
import { isMediaSourceCapable, isMediaTextTrackCapable, isQuerySelectorAllCapable } from "@videojs/media";
//#region src/dom/store/features/text-track.ts
function getTrackId(track, index) {
	return track.id || `track:${index}:${track.kind}:${track.language}:${track.label}`;
}
/**
* Caption/subtitle tracks paired with the ids exposed through `textTrackList`, ordered like the captions menu so
* index-based fallbacks agree with the UI.
*/
function getSubtitlesTracks(media) {
	return Array.from(media.textTracks).map((track, index) => ({
		id: getTrackId(track, index),
		track
	})).filter(({ track }) => isCaptionOrSubtitleTrack(track)).sort((a, b) => a.track.kind > b.track.kind ? 1 : a.track.kind < b.track.kind ? -1 : 0);
}
/** Show at most one caption/subtitle track; passing `null` disables them all. */
function showOnly(tracks, active) {
	for (const { track } of tracks) {
		const mode = track === active ? "showing" : "disabled";
		if (track.mode !== mode) track.mode = mode;
	}
}
/**
* Map a media element's `crossOrigin` to a CORS mode. Per the CORS-settings attribute, any value other than
* `use-credentials` is Anonymous — including the empty string and unknown keywords.
*/
function toCorsMode(value) {
	if (isNil(value)) return null;
	return value.toLowerCase() === "use-credentials" ? "use-credentials" : "anonymous";
}
function findLocaleTrack(tracks, locale) {
	const localeKey = getCanonicalLocaleKey(locale);
	const keys = findLocaleKeys(locale);
	if (localeKey !== DEFAULT_LOCALE && !localeKey.startsWith(`${DEFAULT_LOCALE}-`)) keys.pop();
	for (const key of keys) {
		const exact = tracks.find(({ track }) => getCanonicalLocaleKey(track.language) === key);
		if (exact) return exact;
		const regional = tracks.find(({ track }) => getCanonicalLocaleKey(track.language).startsWith(`${key}-`));
		if (regional) return regional;
	}
}
const textTrackFeature = definePlayerFeature({
	name: "textTrack",
	state: ({ target }) => {
		let lastShownId = null;
		return {
			chaptersCues: [],
			thumbnailCues: [],
			thumbnailTrackSrc: null,
			thumbnailTrackCrossOrigin: null,
			textTrackList: [],
			subtitlesShowing: false,
			toggleSubtitles(forceShow) {
				const { media } = target();
				if (!isMediaTextTrackCapable(media)) return false;
				const subtitlesTracks = getSubtitlesTracks(media);
				if (!subtitlesTracks.length) return false;
				const showing = subtitlesTracks.find(({ track }) => track.mode === "showing");
				const nextShowing = forceShow ?? !showing;
				if (showing) lastShownId = showing.id;
				if (!nextShowing) {
					showOnly(subtitlesTracks, null);
					return false;
				}
				const next = showing ?? subtitlesTracks.find(({ id }) => id === lastShownId) ?? findLocaleTrack(subtitlesTracks, globalThis.navigator?.language ?? "") ?? subtitlesTracks[0];
				lastShownId = next.id;
				showOnly(subtitlesTracks, next.track);
				return true;
			},
			selectSubtitlesTrack(value) {
				const { media } = target();
				if (!isMediaTextTrackCapable(media)) return;
				const subtitlesTracks = getSubtitlesTracks(media);
				if (!subtitlesTracks.length) return;
				if (value === "off") {
					const showing = subtitlesTracks.find(({ track }) => track.mode === "showing");
					if (showing) lastShownId = showing.id;
					showOnly(subtitlesTracks, null);
					return;
				}
				const active = subtitlesTracks.find(({ id }) => id === value);
				if (!active) return;
				lastShownId = active.id;
				showOnly(subtitlesTracks, active.track);
			}
		};
	},
	attach({ target, signal, set }) {
		const { media } = target;
		if (!isMediaTextTrackCapable(media)) return;
		let trackCleanup = null;
		const sync = () => {
			trackCleanup?.abort();
			trackCleanup = new AbortController();
			let chaptersTrack = null;
			let thumbnailTrack = null;
			const textTrackList = [];
			let subtitlesShowing = false;
			for (let i = 0; i < media.textTracks.length; i++) {
				const track = media.textTracks[i];
				if (!chaptersTrack && track.kind === "chapters") chaptersTrack = track;
				if (!thumbnailTrack && track.kind === "metadata" && track.label === "thumbnails") thumbnailTrack = track;
				textTrackList.push({
					id: getTrackId(track, i),
					kind: track.kind,
					label: track.label,
					language: track.language,
					mode: track.mode
				});
				if (isCaptionOrSubtitleTrack(track) && track.mode === "showing") subtitlesShowing = true;
			}
			const chaptersCues = chaptersTrack?.cues ? Array.from(chaptersTrack.cues) : [];
			const thumbnailCues = thumbnailTrack?.cues ? Array.from(thumbnailTrack.cues) : [];
			let thumbnailTrackSrc = null;
			let thumbnailTrackCrossOrigin = null;
			if (thumbnailTrack) {
				thumbnailTrackSrc = findTrackElement(media, thumbnailTrack)?.src ?? null;
				thumbnailTrackCrossOrigin = isMediaSourceCapable(media) ? toCorsMode(media.crossOrigin) : null;
			}
			const tracks = isQuerySelectorAllCapable(media) && media.querySelectorAll("track") || [];
			const shadowTracks = media instanceof HTMLElement && media.shadowRoot?.querySelectorAll("track") || [];
			for (const trackEl of [...tracks, ...shadowTracks]) if (!trackEl.track?.cues?.length) listen(trackEl, "load", sync, { signal: trackCleanup.signal });
			set({
				chaptersCues,
				thumbnailCues,
				thumbnailTrackSrc,
				thumbnailTrackCrossOrigin,
				textTrackList,
				subtitlesShowing
			});
		};
		sync();
		const textTracks = media.textTracks;
		if (textTracks instanceof EventTarget) {
			listen(textTracks, "addtrack", sync, { signal });
			listen(textTracks, "removetrack", sync, { signal });
			listen(textTracks, "change", sync, { signal });
		}
		listen(media, "loadstart", sync, { signal });
		signal.addEventListener("abort", () => trackCleanup?.abort(), { once: true });
	}
});
//#endregion
export { textTrackFeature };

//# sourceMappingURL=text-track.js.map